<template>
  <div class="strain-detail">
    <div v-if="loading" class="loading">
      <div class="spinner"></div>
      <p>Loading strain data...</p>
    </div>
    <div v-else-if="error" class="error">
      <h3>Error Information</h3>
      <p>{{ error }}</p>
      <p class="suggestion">
        Please visit NCBI website for detailed information:
        <a :href="getErrorLink" target="_blank" class="ncbi-link">Click here to visit</a>
      </p>
      <button @click="retryLoading" class="retry-button">Retry</button>
    </div>
    <div v-else class="detail-container">
      <h2>{{ strain.basic_info.organism_name || 'Unknown Strain' }}</h2>
      
      <div class="detail-section">
        <h3>Basic Information</h3>
        <table>
          <tbody>
            <tr>
              <td>Assembly ID:</td>
              <td>{{ strain.assembly_accession }}</td>
            </tr>
            <tr v-if="strain.basic_info.organism_name && strain.basic_info.organism_name !== '未知'">
              <td>Organism Name:</td>
              <td>{{ strain.basic_info.organism_name }}</td>
            </tr>
            <tr v-if="strain.basic_info.infraspecific_name && strain.basic_info.infraspecific_name !== '未知'">
              <td>Infraspecific Name:</td>
              <td>{{ strain.basic_info.infraspecific_name }}</td>
            </tr>
            <tr v-if="strain.basic_info.tax_id && strain.basic_info.tax_id !== '未知'">
              <td>Taxonomy ID:</td>
              <td>
                <a :href="'https://www.ncbi.nlm.nih.gov/taxonomy/' + strain.basic_info.tax_id" 
                   target="_blank" 
                   class="external-link">
                  {{ strain.basic_info.tax_id }}
                </a>
              </td>
            </tr>
            <tr v-if="strain.basic_info.strain && strain.basic_info.strain !== '未知'">
              <td>Strain:</td>
              <td>{{ strain.basic_info.strain }}</td>
            </tr>
            <tr v-if="strain.basic_info.isolate && strain.basic_info.isolate !== '未知'">
              <td>Isolate:</td>
              <td>{{ strain.basic_info.isolate }}</td>
            </tr>
            <tr v-if="strain.basic_info.culture_collection && strain.basic_info.culture_collection !== '未知'">
              <td>Culture Collection:</td>
              <td>{{ strain.basic_info.culture_collection }}</td>
            </tr>
            <tr v-if="strain.basic_info.assembly_level && strain.basic_info.assembly_level !== '未知'">
              <td>Assembly Level:</td>
              <td>{{ strain.basic_info.assembly_level }}</td>
            </tr>
            <tr v-if="strain.basic_info.genome_representation && strain.basic_info.genome_representation !== '未知'">
              <td>Genome Representation:</td>
              <td>{{ strain.basic_info.genome_representation }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div v-if="strain.taxonomy && strain.taxonomy.length > 0" class="detail-section">
        <h3>Classification Information</h3>
        <div class="taxonomy-list">
          {{ strain.taxonomy.join(' > ') }}
        </div>
      </div>

      <div class="detail-section">
        <h3>Assembly Information</h3>
        <table>
          <tbody>
            <tr v-if="strain.assembly_info.assembly_name && strain.assembly_info.assembly_name !== '未知'">
              <td>Assembly Name:</td>
              <td>{{ strain.assembly_info.assembly_name }}</td>
            </tr>
            <tr v-if="strain.assembly_info.assembly_type && strain.assembly_info.assembly_type !== '未知'">
              <td>Assembly Type:</td>
              <td>{{ strain.assembly_info.assembly_type }}</td>
            </tr>
            <tr v-if="strain.assembly_info.assembly_status && strain.assembly_info.assembly_status !== '未知'">
              <td>Assembly Status:</td>
              <td>{{ strain.assembly_info.assembly_status }}</td>
            </tr>
            <tr v-if="strain.assembly_info.wgs_project && strain.assembly_info.wgs_project !== '未知'">
              <td>WGS Project:</td>
              <td>{{ strain.assembly_info.wgs_project }}</td>
            </tr>
            <tr v-if="strain.assembly_info.assembly_method && strain.assembly_info.assembly_method !== '未知'">
              <td>Assembly Method:</td>
              <td>{{ strain.assembly_info.assembly_method }}</td>
            </tr>
            <tr v-if="strain.assembly_info.genome_coverage && strain.assembly_info.genome_coverage !== '未知'">
              <td>Genome Coverage:</td>
              <td>{{ strain.assembly_info.genome_coverage }}</td>
            </tr>
            <tr v-if="strain.assembly_info.sequencing_technology && strain.assembly_info.sequencing_technology !== '未知'">
              <td>Sequencing Technology:</td>
              <td>{{ strain.assembly_info.sequencing_technology }}</td>
            </tr>
            <tr v-if="strain.assembly_info.genome_size && strain.assembly_info.genome_size !== '未知 bp'">
              <td>Genome Size:</td>
              <td>{{ formatNumber(strain.assembly_info.genome_size) }} bp</td>
            </tr>
            <tr v-if="strain.assembly_info.submission_date && strain.assembly_info.submission_date !== '未知' && strain.assembly_info.submission_date !== 'Invalid Date'">
              <td>Submission Date:</td>
              <td>{{ formatDate(strain.assembly_info.submission_date) }}</td>
            </tr>
            <tr v-if="strain.assembly_info.release_date && strain.assembly_info.release_date !== '未知' && strain.assembly_info.release_date !== 'Invalid Date'">
              <td>Release Date:</td>
              <td>{{ formatDate(strain.assembly_info.release_date) }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="detail-section">
        <h3>Genome Statistics</h3>
        <table>
          <tbody>
            <tr v-if="strain.assembly_stats.total_sequence_length && strain.assembly_stats.total_sequence_length !== '未知'">
              <td>Total Sequence Length:</td>
              <td>{{ formatNumber(strain.assembly_stats.total_sequence_length) }} bp</td>
            </tr>
            <tr v-if="strain.assembly_stats.number_of_scaffolds && strain.assembly_stats.number_of_scaffolds !== '未知'">
              <td>Number of Scaffolds:</td>
              <td>{{ formatNumber(strain.assembly_stats.number_of_scaffolds) }}</td>
            </tr>
            <tr v-if="strain.assembly_stats.scaffold_n50 && strain.assembly_stats.scaffold_n50 !== '未知'">
              <td>Scaffold N50:</td>
              <td>{{ formatNumber(strain.assembly_stats.scaffold_n50) }}</td>
            </tr>
            <tr v-if="strain.assembly_stats.scaffold_l50 && strain.assembly_stats.scaffold_l50 !== '未知'">
              <td>Scaffold L50:</td>
              <td>{{ formatNumber(strain.assembly_stats.scaffold_l50) }}</td>
            </tr>
            <tr v-if="strain.assembly_stats.number_of_contigs && strain.assembly_stats.number_of_contigs !== '未知'">
              <td>Number of Contigs:</td>
              <td>{{ formatNumber(strain.assembly_stats.number_of_contigs) }}</td>
            </tr>
            <tr v-if="strain.assembly_stats.contig_n50 && strain.assembly_stats.contig_n50 !== '未知'">
              <td>Contig N50:</td>
              <td>{{ formatNumber(strain.assembly_stats.contig_n50) }}</td>
            </tr>
            <tr v-if="strain.assembly_stats.contig_l50 && strain.assembly_stats.contig_l50 !== '未知'">
              <td>Contig L50:</td>
              <td>{{ formatNumber(strain.assembly_stats.contig_l50) }}</td>
            </tr>
            <tr v-if="strain.assembly_stats.gc_percent && strain.assembly_stats.gc_percent !== '未知'">
              <td>GC Content:</td>
              <td>{{ strain.assembly_stats.gc_percent }}%</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div v-if="hasAnnotationInfo" class="detail-section">
        <h3>Annotation Information</h3>
        <table>
          <tbody>
            <tr v-if="strain.annotation_info.annotation_name && strain.annotation_info.annotation_name !== '未知'">
              <td>Annotation Name:</td>
              <td>{{ strain.annotation_info.annotation_name }}</td>
            </tr>
            <tr v-if="strain.annotation_info.annotation_date && strain.annotation_info.annotation_date !== '未知' && strain.annotation_info.annotation_date !== 'Invalid Date'">
              <td>Annotation Date:</td>
              <td>{{ formatDate(strain.annotation_info.annotation_date) }}</td>
            </tr>
            <tr v-if="strain.annotation_info.software_version && strain.annotation_info.software_version !== '未知'">
              <td>Software Version:</td>
              <td>{{ strain.annotation_info.software_version }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div v-if="hasGenomeFeatures" class="detail-section">
        <h3>Genome Features</h3>
        <table>
          <tbody>
            <tr v-if="strain.genome_features.total_genes && strain.genome_features.total_genes !== '未知'">
              <td>Total Genes:</td>
              <td>{{ formatNumber(strain.genome_features.total_genes) }}</td>
            </tr>
            <tr v-if="strain.genome_features.protein_coding_genes && strain.genome_features.protein_coding_genes !== '未知'">
              <td>Protein Coding Genes:</td>
              <td>{{ formatNumber(strain.genome_features.protein_coding_genes) }}</td>
            </tr>
            <tr v-if="strain.genome_features.non_coding_genes && strain.genome_features.non_coding_genes !== '未知'">
              <td>Non-coding Genes:</td>
              <td>{{ formatNumber(strain.genome_features.non_coding_genes) }}</td>
            </tr>
            <tr v-if="strain.genome_features.pseudo_genes && strain.genome_features.pseudo_genes !== '未知'">
              <td>Pseudo Genes:</td>
              <td>{{ formatNumber(strain.genome_features.pseudo_genes) }}</td>
            </tr>
            <tr v-if="strain.genome_features.rrna_genes && strain.genome_features.rrna_genes !== '未知'">
              <td>rRNA Genes:</td>
              <td>{{ formatNumber(strain.genome_features.rrna_genes) }}</td>
            </tr>
            <tr v-if="strain.genome_features.trna_genes && strain.genome_features.trna_genes !== '未知'">
              <td>tRNA Genes:</td>
              <td>{{ formatNumber(strain.genome_features.trna_genes) }}</td>
            </tr>
            <tr v-if="strain.genome_features.other_rna_genes && strain.genome_features.other_rna_genes !== '未知'">
              <td>Other RNA Genes:</td>
              <td>{{ formatNumber(strain.genome_features.other_rna_genes) }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div v-if="hasRelatedData" class="detail-section">
        <h3>Related Data</h3>
        <table>
          <tr v-if="strain.related_data.biosample && strain.related_data.biosample.accession">
            <td>BioSample:</td>
            <td>
              <a :href="'https://www.ncbi.nlm.nih.gov/biosample/' + strain.related_data.biosample.accession"
                 target="_blank"
                 class="external-link">
                {{ strain.related_data.biosample.accession }}
              </a>
            </td>
          </tr>
          <tr v-if="strain.related_data.bioproject && strain.related_data.bioproject.accession">
            <td>BioProject:</td>
            <td>
              <a :href="'https://www.ncbi.nlm.nih.gov/bioproject/' + strain.related_data.bioproject.accession"
                 target="_blank"
                 class="external-link">
                {{ strain.related_data.bioproject.accession }}
              </a>
            </td>
          </tr>
        </table>
      </div>

      <div class="detail-section">
        <h3>External Links</h3>
        <div class="links">
          <a :href="getNcbiLink" target="_blank" class="ncbi-link">View Complete Information on NCBI</a>
        </div>
      </div>

      <div class="detail-section">
        <h3>Download</h3>
        <div class="download-buttons">
          <el-button
            type="primary"
            @click="downloadGenome('fasta')"
            :loading="downloadingFasta"
          >
            Genome Sequence (FASTA)
          </el-button>
          <el-button
            type="primary"
            @click="downloadGenome('gbff')"
            :loading="downloadingGbff"
          >
            Sequence and Annotation (GBFF)
          </el-button>
          <el-button
            type="primary"
            @click="downloadGenome('faa')"
            :loading="downloadingFaa"
          >
            Protein Sequences (FAA)
          </el-button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, computed } from 'vue'
import axios from 'axios'
import { ElMessage } from 'element-plus';

export default {
  name: 'StrainDetail',
  props: {
    assemblyAccession: {
      type: String,
      required: true
    }
  },
  emits: ['error'],
  setup(props, { emit }) {
    const strain = ref(null);
    const loading = ref(true);
    const error = ref(null);
    const downloadingFasta = ref(false);
    const downloadingGbff = ref(false);
    const downloadingFaa = ref(false);

    const loadStrainData = async () => {
      loading.value = true;
      error.value = null;
      
      try {
        console.log('Loading strain data:', props.assemblyAccession);
        const response = await axios.get(`/api/strain/${props.assemblyAccession}/`);
        
        if (response.data) {
          strain.value = response.data;
          console.log('Data loaded successfully:', strain.value);
        } else {
          throw new Error('Failed to fetch data');
        }
      } catch (e) {
        console.error('Error loading data:', e);
        const errorMessage = e.response?.data?.error || 'Failed to fetch strain data';
        error.value = errorMessage;
        emit('error', errorMessage);
      } finally {
        loading.value = false;
      }
    };

    const retryLoading = () => {
      loadStrainData();
    };

    const getErrorLink = computed(() => {
      return `https://www.ncbi.nlm.nih.gov/datasets/genome/${props.assemblyAccession}`;
    });

    const getNcbiLink = computed(() => {
      return `https://www.ncbi.nlm.nih.gov/datasets/genome/${props.assemblyAccession}`;
    });

    // 格式化数字
    const formatNumber = (num) => {
      if (num === null || num === undefined) return 'Unknown';
      return num.toLocaleString();
    };

    // 格式化日期
    const formatDate = (date) => {
      if (!date) return 'Unknown';
      return new Date(date).toLocaleDateString();
    };

    const downloadGenome = async (fileType) => {
      let loadingRef;
      switch(fileType) {
        case 'fasta':
          loadingRef = downloadingFasta;
          break;
        case 'gbff':
          loadingRef = downloadingGbff;
          break;
        case 'faa':
          loadingRef = downloadingFaa;
          break;
        default:
          loadingRef = downloadingFasta;
      }
      
      loadingRef.value = true;
      
      try {
        const response = await axios.get(
          `/api/genome/${strain.value.assembly_accession}/download/${fileType}/`,
          { responseType: 'blob' }
        );
        
        let fileExtension = fileType;
        if (fileType === 'fasta') {
          fileExtension = 'fna';
        }
        
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `${strain.value.assembly_accession}_${fileExtension}.zip`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } catch (error) {
        console.error(`Error downloading ${fileType} file:`, error);
        ElMessage.error(`Failed to download ${fileType.toUpperCase()} file`);
      } finally {
        loadingRef.value = false;
      }
    };

    const hasAnnotationInfo = computed(() => {
      const info = strain.value?.annotation_info;
      return info && Object.values(info).some(val => val && val !== '未知' && val !== 'Invalid Date');
    });

    const hasGenomeFeatures = computed(() => {
      const features = strain.value?.genome_features;
      return features && Object.values(features).some(val => val && val !== '未知');
    });

    const hasRelatedData = computed(() => {
      const data = strain.value?.related_data;
      return data && (
        (data.biosample && data.biosample.accession) || 
        (data.bioproject && data.bioproject.accession)
      );
    });

    onMounted(() => {
      loadStrainData();
    });

    return {
      strain,
      loading,
      error,
      downloadingFasta,
      downloadingGbff,
      downloadingFaa,
      retryLoading,
      getErrorLink,
      getNcbiLink,
      formatNumber,
      formatDate,
      downloadGenome,
      hasAnnotationInfo,
      hasGenomeFeatures,
      hasRelatedData
    };
  }
};
</script>

<style scoped>
.strain-detail {
  padding: 20px;
}

.detail-container {
  max-width: 1000px;
  margin: 0 auto;
}

.detail-section {
  margin-bottom: 30px;
  background: #f8f9fa;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.05);
}

.detail-section h3 {
  color: #2c3e50;
  margin-bottom: 15px;
  padding-bottom: 8px;
  border-bottom: 2px solid #3498db;
}

table {
  width: 100%;
  border-collapse: collapse;
}

td {
  padding: 12px;
  border-bottom: 1px solid #e9ecef;
  line-height: 1.5;
}

td:first-child {
  font-weight: 600;
  width: 250px;
  color: #495057;
}

.taxonomy-list {
  padding: 15px;
  background: #fff;
  border-radius: 4px;
  line-height: 1.6;
  color: #2c3e50;
}

.reference {
  margin-bottom: 20px;
  padding: 15px;
  background: #fff;
  border-radius: 4px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.05);
}

.reference:last-child {
  margin-bottom: 0;
}

.authors {
  font-weight: 600;
  margin-bottom: 8px;
  color: #2c3e50;
}

.title {
  color: #2c3e50;
  margin-bottom: 8px;
  line-height: 1.4;
}

.journal {
  color: #6c757d;
  font-style: italic;
  margin-bottom: 8px;
}

.external-link, .pubmed-link {
  color: #3498db;
  text-decoration: none;
  transition: color 0.3s;
}

.external-link:hover, .pubmed-link:hover {
  color: #2980b9;
  text-decoration: underline;
}

.loading {
  text-align: center;
  padding: 40px;
}

.spinner {
  width: 40px;
  height: 40px;
  margin: 0 auto 20px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #3498db;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.error {
  text-align: center;
  padding: 20px;
  color: #e74c3c;
  background: #fde8e7;
  border-radius: 8px;
  margin: 20px;
}

.retry-button {
  margin-top: 15px;
  padding: 8px 20px;
  background-color: #3498db;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.3s;
}

.retry-button:hover {
  background-color: #2980b9;
}

.ncbi-link {
  display: inline-block;
  padding: 10px 20px;
  background-color: #3498db;
  color: white;
  text-decoration: none;
  border-radius: 4px;
  transition: background-color 0.3s;
}

.ncbi-link:hover {
  background-color: #2980b9;
}

.links {
  margin-top: 10px;
  display: flex;
  gap: 10px;
}

.download-section {
  background: #f8fafc;
  border-radius: 12px;
  padding: 24px;
  margin: 20px 0;
}

.download-section h2 {
  color: #1e293b;
  font-size: 1.5rem;
  margin-bottom: 20px;
  font-weight: 600;
}

.download-buttons {
  display: flex;
  gap: 16px;
  margin-top: 16px;
}

.download-buttons .el-button {
  min-width: 200px;
}
</style> 